import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { AuthorByline } from './AuthorByline';
import { articleUrl, itemVariants, sectionVariants } from './constants';
import type { JournalCurrentSection, JournalGuide } from './types';

/** Compact side-column card: small thumbnail + text */
function SideCard({ guide }: { guide: JournalGuide }) {
    const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group block border-b border-[#F5F5F5]/10 pb-7 last:border-0">
            {guide.main_image && (
                <div className="mb-3 aspect-video w-full overflow-hidden">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            {tag && <span className="lux-caps lux-caps-sm mb-1.5 block text-[#F5F5F5]/50">{tag}</span>}
            <h3 className="lux-didot lux-didot-md line-clamp-2 text-[#F5F5F5] transition-colors group-hover:text-[#F5F5F5]/80">{guide.title}</h3>
            {guide.author_name && <AuthorByline name={guide.author_name} className="mt-2 text-[#F5F5F5]/55" />}
        </Link>
    );
}

/** Latest column card: title-only with small thumbnail on the right */
function LatestCard({ guide }: { guide: JournalGuide }) {
    const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group flex items-center gap-4 border-b border-[#F5F5F5]/10 py-4 last:border-0">
            <div className="flex-1 text-left">
                {tag && <span className="lux-caps lux-caps-sm mb-1 block text-[#F5F5F5]/50">{tag}</span>}
                <h4 className="lux-didot lux-didot-md line-clamp-3 text-[#F5F5F5] transition-colors group-hover:text-[#F5F5F5]/80">{guide.title}</h4>
                {guide.author_name && <AuthorByline name={guide.author_name} className="mt-1.5 text-[#F5F5F5]/55" />}
            </div>
            {guide.main_image && (
                <div className="aspect-[3/4] w-16 shrink-0 overflow-hidden">
                    <img src={guide.main_image} alt={guide.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
            )}
        </Link>
    );
}

interface CategoryHeroProps {
    currentSection: JournalCurrentSection;
    hero: JournalGuide | null;
    featured: JournalGuide[];
}

export default function CategoryHero({ currentSection, hero, featured }: CategoryHeroProps) {
    const shouldReduceMotion = useReducedMotion();

    if (!hero) return null;

    const tag = hero.kicker ?? hero.country_region ?? hero.tags?.[0]?.name;

    // Split featured into left column (editor picks) and right column (latest)
    const editorPicks = featured.slice(0, 2);
    const latest = featured.slice(2);

    return (
        <motion.section
            className="bg-[#0e2727]"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            {/* Category heading — centered */}
            <div className="lux-space-px">
                <div className="mx-auto max-w-[1400px] pt-12 pb-10 text-center lg:pt-16 lg:pb-12">
                    <motion.div variants={shouldReduceMotion ? undefined : itemVariants}>
                        {currentSection.kicker && <span className="lux-caps lux-caps-sm mb-3 block text-[#F5F5F5]/50">{currentSection.kicker}</span>}
                        <h1 className="lux-didot text-[#F5F5F5]" style={{ fontSize: 'clamp(2.25rem, 6vw, 4.5rem)', lineHeight: 1.05 }}>
                            {currentSection.name}
                        </h1>
                        <div className="mx-auto mt-5 h-px w-16 bg-[#F5F5F5]/25" />
                    </motion.div>
                </div>
            </div>

            {/* Three-column editorial layout */}
            <div className="lux-space-px pb-10 lg:pb-16">
                <div className="mx-auto grid max-w-[1400px] gap-8 lg:grid-cols-[1fr_1.6fr_1fr] lg:gap-10">
                    {/* Left column — Editor Picks */}
                    {editorPicks.length > 0 && (
                        <motion.div className="order-2 space-y-6 lg:order-1" variants={shouldReduceMotion ? undefined : itemVariants}>
                            {editorPicks.map((guide) => (
                                <SideCard key={guide.id} guide={guide} />
                            ))}
                        </motion.div>
                    )}

                    {/* Center column — Featured / Hero card */}
                    <motion.div
                        className={cn('order-1 lg:order-2', !editorPicks.length && 'lg:col-span-2')}
                        variants={shouldReduceMotion ? undefined : itemVariants}
                    >
                        <Link href={articleUrl(hero.slug)} className="group">
                            {hero.main_image && (
                                <div className="aspect-video w-full overflow-hidden">
                                    <img
                                        src={hero.main_image}
                                        alt={hero.title}
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                                    />
                                </div>
                            )}
                            <div className="mt-4">
                                {tag && <span className="lux-caps lux-caps-sm text-[#F5F5F5]/50">{tag}</span>}
                                <h2 className="lux-didot lux-didot-lg mt-1 line-clamp-2 text-[#F5F5F5] transition-colors group-hover:text-[#F5F5F5]/80">
                                    {hero.title}
                                </h2>
                                {hero.excerpt && <p className="lux-body lux-body-sm mt-3 line-clamp-3 text-[#F5F5F5]/60">{hero.excerpt}</p>}
                                {hero.author_name && <AuthorByline name={hero.author_name} className="mt-3 text-[#F5F5F5]/55" />}
                            </div>
                        </Link>
                    </motion.div>

                    {/* Right column — Latest */}
                    {latest.length > 0 && (
                        <motion.div className="order-3 space-y-0" variants={shouldReduceMotion ? undefined : itemVariants}>
                            {latest.map((guide) => (
                                <LatestCard key={guide.id} guide={guide} />
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.section>
    );
}
